export type SeoPageKind = 'industry' | 'guide' | 'case-study';

export type SeoPageSection = {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
};

export type SeoPageLink = {
  label: string;
  href: string;
};

export type SeoPage = {
  path: string;
  kind: SeoPageKind;
  title: string;
  description: string;
  eyebrow: string;
  h1: string;
  intro: string;
  directAnswer?: string;
  sections: SeoPageSection[];
  relatedLinks: SeoPageLink[];
};

const buildGuidance = {
  heading: 'What it costs and how long it takes',
  paragraphs: [
    'A focused single-workflow tool typically runs $15,000 to $25,000. An operational platform with roles, notifications, payments, and mobile typically runs $35,000 to $60,000. A multi-tenant SaaS product intended for sale starts at $60,000 and up.',
    'Atlas AG starts with paid discovery: $3,500 for two weeks, producing a spec, wireframes, and a fixed quote. That fee is credited against the build. Most builds go live in six to ten weeks once the first release is defined.',
  ],
};

export const seoPages: SeoPage[] = [
  {
    path: '/software-for-construction-companies',
    kind: 'industry',
    title: 'Custom Construction Management Software | Atlas AG',
    description:
      'Custom construction management software for crews that need scheduling, equipment, field operations, and project information to fit the way work actually runs.',
    eyebrow: 'INDUSTRY / CONSTRUCTION',
    h1: 'Construction software that works where construction happens.',
    intro:
      'Construction companies rarely have a single software problem. They have a chain of small handoffs that breaks under pressure: a superintendent needs the current plan, a crew needs the right information on a phone, equipment needs service before it stops a job, and the office needs to know what happened without calling five people. Custom construction management software connects those handoffs around the way the company already operates.',
    sections: [
      {
        heading: 'The specific failure',
        paragraphs: [
          'A dispatcher or project manager often maintains the real schedule in a whiteboard, a spreadsheet, and a stream of text messages. The system of record says one thing while the jobsite knows another. Hours, equipment status, material needs, and change information arrive late because the person closest to the work has the least convenient way to record it.',
          'That creates expensive ambiguity. A crew shows up without the right information. A machine is used past a service interval. A change order sits in an inbox while production moves on. The problem is not that a construction company lacks software. The problem is that the software is not shaped around the decisions people make during a normal workday.',
        ],
      },
      {
        heading: 'Why off-the-shelf tools fail on the jobsite',
        paragraphs: [
          'Generic construction platforms tend to assume consistent connectivity, desktop-first administration, and a workflow that can be configured without changing its vocabulary. Real crews work with gloves, dirty screens, intermittent service, shared equipment, and very little patience for a form that asks for information nobody uses.',
          'Per-seat pricing is also a poor fit when a business has a small office, seasonal crews, subcontractors, and operators who need one narrow part of the system. A good fit-built system gives each role the right amount of access instead of making every person navigate a miniature enterprise platform.',
        ],
      },
      {
        heading: 'What a fit-built construction system does instead',
        paragraphs: [
          'The first release should make one expensive workflow reliable, then give the business a foundation for the next one. That may mean scheduling crews and equipment from the same view, capturing field updates with short forms, or giving a project manager a clear exception list instead of another dashboard full of stale status.',
        ],
        bullets: [
          'Mobile-first field updates with the fewest possible taps.',
          'Role-aware views for owners, project managers, dispatchers, and operators.',
          'Equipment, service, and inspection records connected to the work using them.',
          'Notifications when a decision needs attention, not when a database row changes.',
          'A customer or internal portal that uses the company\'s language and approval steps.',
        ],
      },
      {
        heading: 'Proof that the details matter',
        paragraphs: [
          'MaintainTime is a useful example of this approach. Its equipment maintenance workflow supports service intervals by hours, mileage, or calendar; fuel tracking; service history; and alerts before something breaks. QR codes on each machine give an operator in gloves a fast way into the record, and English and Spanish are available throughout. It runs as a web app on a phone, so a twenty-person crew does not need an app-store installation.',
          'Those are not decorative features. They are decisions about where construction work happens, who touches the software, and what information is worth capturing. A construction platform should be judged by the moment a crew is busy, not by how many configuration screens it has.',
        ],
      },
      buildGuidance,
      {
        heading: 'Start with the workflow that costs the most',
        paragraphs: [
          'Bring the current schedule, field forms, equipment records, spreadsheets, and the workarounds people use when the official tool gets in the way. Atlas AG can map the real handoffs, separate the first release from later ideas, and put a fixed quote around the part that will create the most operational leverage.',
          'The right custom construction management software does not ask a crew to become a software company. It quietly makes the existing operation more legible, more dependable, and easier to hand to the next person.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'See the MaintainTime equipment maintenance case study', href: '/work/maintaintime' },
      { label: 'Read what custom software costs', href: '/what-custom-software-costs' },
      { label: 'Tell us what needs to exist', href: '/#contact' },
    ],
  },
  {
    path: '/software-for-landscaping-companies',
    kind: 'industry',
    title: 'Custom Software for Landscaping Companies | Atlas AG',
    description:
      'Custom landscaping business software for routing, crews, equipment, recurring work, estimates, and customer communication that fits the operation.',
    eyebrow: 'INDUSTRY / LANDSCAPING',
    h1: 'Landscaping software for the work between the addresses.',
    intro:
      'Landscaping operations look simple from the outside: crews go to properties and do the work. Inside the business, every day is a coordination problem. Routes change, weather moves the schedule, equipment travels between teams, a property needs a specific treatment, and the office has to keep customers informed while protecting margin. Custom landscaping business software can connect those moving parts without forcing the company into a generic field-service workflow.',
    sections: [
      {
        heading: 'The specific failure',
        paragraphs: [
          'A landscaping company often has a recurring schedule that is only partly recurring. The route is planned in one place, crew notes are in another, estimates are sent from an inbox, and the details that make a property profitable live in someone\'s memory. When a crew member calls in sick or a storm changes the day, a manager rebuilds the plan by hand.',
          'The hidden cost is not only the time spent scheduling. It is the lost context. A new crew arrives without knowing the gate code, the irrigation issue, the material preference, or what the customer was promised. The company looks busy but cannot easily answer which work was completed, which work needs a return visit, and which jobs are quietly losing money.',
        ],
      },
      {
        heading: 'Why generic field-service software misses',
        paragraphs: [
          'Many field-service products are designed around invoices and appointments rather than the physical reality of a landscaping route. They can store an address, but not necessarily the site instructions, recurring scope, seasonal changes, crew capability, equipment assignment, and exception that matter together.',
          'A business with a mix of maintenance, installs, cleanups, irrigation, and commercial work also gets punished by rigid job types. The system becomes a second vocabulary the office has to translate. Seasonal crews make per-seat pricing and long onboarding especially painful when some people need a simple mobile checklist and nothing more.',
        ],
      },
      {
        heading: 'What a fit-built system does instead',
        paragraphs: [
          'Start with the route or work order that generates the most coordination. Let the office see the day as a sequence of decisions, and let the crew see only the information needed to complete the property safely and consistently.',
        ],
        bullets: [
          'Recurring work that can be adjusted without destroying the underlying service plan.',
          'Crew and equipment assignments that expose conflicts before the route is published.',
          'Property notes, photos, access details, and customer commitments in one place.',
          'Estimate and quote workflows that reflect maintenance, installs, and seasonal work.',
          'Completion updates that trigger the right customer message or office follow-up.',
        ],
      },
      {
        heading: 'A useful production pattern',
        paragraphs: [
          'MaintainTime demonstrates the kind of operational detail that matters to a landscaping business. Service intervals can be based on hours, mileage, or calendar; operators can reach a machine record with a QR code; service history and fuel tracking stay with the equipment; and English and Spanish are available throughout. That pattern applies beyond maintenance: the interface should respect the setting, role, and moment in which the information is captured.',
          'A fit-built landscaping system does not need to copy every feature of a large field-service suite. It needs to protect the handoffs that keep a route profitable and a customer confident.',
        ],
      },
      buildGuidance,
      {
        heading: 'Build around the season you actually run',
        paragraphs: [
          'The best starting conversation is not a list of desired features. It is a walk through a busy day: how work enters the business, how a route changes, what the crew records, what the office chases, and where an owner discovers a problem too late. That gives Atlas AG enough context to scope a first release around a measurable operational improvement.',
          'Discovery costs $3,500 for two weeks and produces a spec, wireframes, and a fixed quote that is credited against the build. The goal is a working system that fits the operation, not a long implementation project that asks the operation to fit the software.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'See the equipment maintenance work in production', href: 'https://maintaintime.com' },
      { label: 'Compare build and buy for field operations', href: '/build-vs-buy-field-operations' },
      { label: 'Start a landscaping software conversation', href: '/#contact' },
    ],
  },
  {
    path: '/software-for-logging-operations',
    kind: 'industry',
    title: 'Logging Operation Management Software | Atlas AG',
    description:
      'Custom logging operation management software for equipment, service intervals, crews, field records, and the information that moves between woods and office.',
    eyebrow: 'INDUSTRY / LOGGING',
    h1: 'Logging software that respects the distance between the woods and the office.',
    intro:
      'Logging operations coordinate expensive equipment, changing terrain, seasonal access, crews, loads, service, and safety information across a workplace that is rarely next to the office. The best logging operation management software is not a larger spreadsheet or a generic dashboard. It is a focused system that makes the important field facts available when a person needs them and turns them into decisions the office can act on.',
    sections: [
      {
        heading: 'The specific failure',
        paragraphs: [
          'The real status of a logging operation may be distributed across radio calls, paper notes, text messages, fuel receipts, machine hours, and one person who knows which detail is current. A loader is down, a road is inaccessible, a crew has moved, or a service interval is approaching, but the information reaches the office only after the plan has already been affected.',
          'That delay compounds. A machine that misses maintenance can affect a whole crew. A load record without context is hard to reconcile. A manager who cannot see the latest field update spends time asking for status instead of making the next decision.',
        ],
      },
      {
        heading: 'Why off-the-shelf software is a poor fit',
        paragraphs: [
          'Desktop-first tools assume stable connectivity and clean data entry. Generic fleet tools may track an asset but not the way an operator, mechanic, supervisor, and owner each need to interact with it. Large platforms also tend to present every role with the same vocabulary, even though a field crew needs speed and the office needs reconciliation.',
          'The right question is not whether a product has a logging module. It is whether the system can handle the actual handoff between a remote machine, a crew lead, a mechanic, a dispatcher, and the person responsible for the result.',
        ],
      },
      {
        heading: 'What a fit-built logging system does instead',
        paragraphs: [
          'A first release should create one trusted operational thread. For one company that could be equipment readiness; for another it could be production and load records; for another it could be the flow of daily updates into the office plan.',
        ],
        bullets: [
          'Fast mobile records that tolerate field conditions and intermittent attention.',
          'Machine identity, hours, mileage, fuel, inspections, and service history connected together.',
          'Role-specific views for operators, mechanics, supervisors, and office staff.',
          'Exception alerts for overdue service, missing records, or a plan that needs a decision.',
          'Exports and ownership of the underlying data instead of a locked reporting layer.',
        ],
      },
      {
        heading: 'Proof in equipment maintenance',
        paragraphs: [
          'MaintainTime was built around operations that run on machines. It supports service intervals by hours, mileage, or calendar, along with fuel tracking, service history, and alerts before a failure. QR codes on the equipment give field crews a direct path to the correct record, and English and Spanish throughout the product make the workflow more usable across a crew.',
          'The important lesson is the shape of the system. An operator should not have to navigate an office application to record a field fact. A manager should not have to reconstruct that fact from a message thread. Each role gets a path that fits the work and contributes to the same record.',
        ],
      },
      buildGuidance,
      {
        heading: 'Make the first release survive a real day',
        paragraphs: [
          'Bring a recent week of dispatch notes, maintenance records, spreadsheets, and exceptions that required a phone call. Those artifacts show more than a requirements checklist: they reveal which facts are repeated, which decisions are delayed, and where the operation is paying for uncertainty.',
          'Paid discovery is $3,500 for two weeks, credited against the build. It produces wireframes, a specification, and a fixed quote. Most first releases go live in six to ten weeks once the scope is clear, with client-owned infrastructure as the default and managed hosting available separately.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'Read the MaintainTime case study', href: '/work/maintaintime' },
      { label: 'See why off-the-shelf field software fails', href: '/why-off-the-shelf-fails-field-operations' },
      { label: 'Talk through a logging workflow', href: '/#contact' },
    ],
  },
  {
    path: '/software-for-equipment-rental',
    kind: 'industry',
    title: 'Custom Equipment Rental Software | Atlas AG',
    description:
      'Custom equipment rental software for inventory, availability, service, delivery, returns, and customer communication across a rental operation.',
    eyebrow: 'INDUSTRY / EQUIPMENT RENTAL',
    h1: 'Equipment rental software for the moment a machine changes hands.',
    intro:
      'Equipment rental businesses manage more than an inventory list. They manage the changing state of an asset: available, reserved, out, late, damaged, awaiting inspection, in service, or ready for the next customer. Custom equipment rental software makes those states visible to the right people and connects the customer promise to what the yard can actually deliver.',
    sections: [
      {
        heading: 'The specific failure',
        paragraphs: [
          'A rental can look available in one spreadsheet while it is reserved in an email, out on a job, waiting for a repair, or missing an inspection. Staff know the nuance because they talk to each other, but the customer sees a yes-or-no answer. When the internal record is late, the company either overpromises or spends time checking every exception by hand.',
          'The same problem appears at return. A machine comes back with hours, fuel, damage, and a service need, but those facts are recorded in different places. The next reservation is made before the asset is truly ready, and a small handoff error becomes a late delivery or a disputed charge.',
        ],
      },
      {
        heading: 'Why generic inventory tools are not enough',
        paragraphs: [
          'A stock system treats an item as present or absent. A rental operation needs a timeline, a condition record, a service state, a delivery commitment, and a customer relationship. Traditional rental software may cover the broad workflow but still force the team to work around terminology, approval steps, or a mobile experience that does not match the yard.',
          'Per-seat systems can also be awkward for a business with counter staff, drivers, mechanics, managers, and customers who each need different slices of the workflow. A purpose-fit system can make those boundaries explicit without turning every user into a full administrator.',
        ],
      },
      {
        heading: 'What a fit-built rental system does instead',
        paragraphs: [
          'Start with the state transitions that create the most risk: reservation to dispatch, dispatch to return, or return to ready. The first release should give the business one accurate view of what can be promised and what has to happen before it can be promised.',
        ],
        bullets: [
          'Availability that reflects reservations, service holds, inspections, and delivery windows.',
          'Simple check-out and return workflows for hours, fuel, condition, and damage.',
          'Maintenance records connected to the equipment instead of a separate log.',
          'Customer and staff views with different permissions and useful vocabulary.',
          'Alerts for overdue returns, inspection failures, and service before the next booking.',
        ],
      },
      {
        heading: 'The equipment record is the product',
        paragraphs: [
          'MaintainTime shows how a machine record becomes useful when it is designed around the people touching the machine. Service intervals can follow hours, mileage, or calendar; QR access gets a crew to the correct asset; and the record includes fuel, service history, and alerts. A rental system can apply the same principle to the full rental lifecycle.',
          'The goal is not to collect more data. It is to make the next decision obvious: can this unit go out, who is responsible for it, what needs to happen before it does, and what should the customer be told?',
        ],
      },
      buildGuidance,
      {
        heading: 'Scope the yard before choosing features',
        paragraphs: [
          'A useful discovery conversation follows one asset through reservation, preparation, delivery, use, return, inspection, service, and the next booking. That exposes where the current tools lose time and which state should become the center of the first release.',
          'Atlas AG works with fixed scope, fixed price, and fixed date. Paid discovery is $3,500 for two weeks and is credited against the build. The finished software is deployed to infrastructure the client owns by default; Atlas AG Managed is available when the client prefers a separate monthly hosting and support service.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'See the equipment maintenance product in production', href: 'https://maintaintime.com' },
      { label: 'Read what to ask before hiring a developer', href: '/questions-before-hiring-a-developer' },
      { label: 'Describe an equipment rental workflow', href: '/#contact' },
    ],
  },
  {
    path: '/software-for-agricultural-operations',
    kind: 'industry',
    title: 'Custom Farm Management Software | Atlas AG',
    description:
      'Custom farm management software for equipment, field operations, records, scheduling, compliance, and the seasonal workflows that generic tools miss.',
    eyebrow: 'INDUSTRY / AGRICULTURE',
    h1: 'Agricultural software built around the season, not the spreadsheet.',
    intro:
      'Agricultural operations work on a calendar, but not a simple one. Weather, soil, equipment, crews, fields, inputs, vendors, compliance, and harvest decisions interact constantly. Custom farm management software can turn the records an operation already keeps into a useful operational system without pretending every farm runs the same way.',
    sections: [
      {
        heading: 'The specific failure',
        paragraphs: [
          'The farm\'s real operating system often combines paper notebooks in a cab, spreadsheets in the office, equipment records in a folder, and knowledge that lives with the person who has worked a field for years. Those tools are flexible, but they are difficult to search, difficult to hand off, and easy to leave out of the next decision.',
          'A manager may need to answer which field received a treatment, which machine is ready, what was done after a weather event, or which compliance document is current. If the answer requires asking three people and reconciling two spreadsheets, the operation is spending its limited attention on retrieval instead of production.',
        ],
      },
      {
        heading: 'Why generic farm platforms fall short',
        paragraphs: [
          'Agricultural software often starts with a broad model of the farm and asks the operation to configure itself around it. That can work for standard records, but it breaks when a business has a specific crop mix, custom treatment workflow, unique equipment practice, or a compliance process that does not match the vendor\'s labels.',
          'Connectivity and seasonality matter too. A field workflow needs to be understandable on a phone and resilient when the user is busy. The office needs a different view: exceptions, missing records, upcoming work, and the evidence needed for a decision or audit.',
        ],
      },
      {
        heading: 'What a fit-built agricultural system does instead',
        paragraphs: [
          'A good first release follows one real production loop from plan to field record to office decision. It does not try to model every possibility on day one. It makes the repeated, expensive, and currently invisible part of the operation reliable.',
        ],
        bullets: [
          'Field records tied to the field, crop, equipment, person, and date that matter.',
          'Seasonal work plans that can change without losing the original record.',
          'Equipment readiness and maintenance connected to the schedule that depends on it.',
          'Simple evidence capture for compliance, quality, and customer or vendor communication.',
          'Role-aware dashboards that distinguish field work from office decisions.',
        ],
      },
      {
        heading: 'Operational detail is the differentiator',
        paragraphs: [
          'MaintainTime is an example of the level of specificity a useful agricultural tool needs. Service intervals can be set by hours, mileage, or calendar; fuel and full service history stay with the machine; alerts arrive before a failure; and QR access lets field crews reach the record without searching. English and Spanish support is part of the operating workflow, not an afterthought.',
          'For agriculture, the equivalent details might be a field boundary, a treatment window, a harvest condition, or a piece of evidence needed later. The product earns its place by honoring those details and making the next action easier.',
        ],
      },
      buildGuidance,
      {
        heading: 'Start with a season you can explain',
        paragraphs: [
          'The most useful discovery material is not an ideal process diagram. It is a recent season with the exceptions left in: a weather delay, a machine problem, a missing record, a compliance request, or a handoff that depended on a phone call. Those moments show what the software has to protect.',
          'Atlas AG uses a two-week paid discovery for $3,500, credited against the build. The output is a specification, wireframes, and a fixed quote. Most builds go live in six to ten weeks, with client-owned infrastructure as the default and optional managed service after launch.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'Read the MaintainTime equipment case study', href: '/work/maintaintime' },
      { label: 'See the custom software cost ranges', href: '/what-custom-software-costs' },
      { label: 'Talk through an agricultural workflow', href: '/#contact' },
    ],
  },
  {
    path: '/software-for-trade-contractors',
    kind: 'industry',
    title: 'Custom Contractor Scheduling Software | Atlas AG',
    description:
      'Custom contractor scheduling software for electrical, plumbing, HVAC, and trade businesses that need dispatch, estimates, crews, and customer updates to fit.',
    eyebrow: 'INDUSTRY / TRADE CONTRACTORS',
    h1: 'Trade contractor software for the work that does not fit a ticket.',
    intro:
      'Trade contractors need to turn a call, quote, appointment, crew, part, and field result into a profitable job. The workflow is familiar, but each business has its own service area, specialties, approval rules, recurring customers, and definition of a complete record. Custom contractor scheduling software makes the system reflect those differences instead of forcing a plumbing, electrical, HVAC, or specialty contractor into the same generic ticketing model.',
    sections: [
      {
        heading: 'The specific failure',
        paragraphs: [
          'A customer calls with a problem that sounds simple but needs context: an address, a system, a safety issue, a warranty, a preferred time, and a person qualified to do the work. The office turns that into a schedule, but the crew may receive only a short description. After the visit, notes, photos, parts, time, and follow-up need to return to the office before the next promise is made.',
          'When those handoffs happen in separate tools, the company loses margin in small amounts. A quote is delayed. A return visit is missed. A technician does not have the history. The owner cannot see which jobs are waiting on an approval or which type of work keeps consuming more time than it sells.',
        ],
      },
      {
        heading: 'Why generic scheduling tools fail',
        paragraphs: [
          'A calendar is not a dispatch system, and a ticket is not the full relationship with a customer. Generic tools often optimize for appointment volume while leaving qualification, skill matching, part readiness, estimate approval, and closeout to a separate process.',
          'The result is a system that looks organized from the office but does not help the person doing the work. Trade contractors also pay for broad seat counts even when an apprentice, subcontractor, customer, or office user needs a much smaller interaction.',
        ],
      },
      {
        heading: 'What a fit-built trade system does instead',
        paragraphs: [
          'The first release should connect the few steps where a missed detail becomes a missed margin. It can be a dispatch-to-field workflow, estimate approval, recurring service, or a closeout process that makes billing and follow-up dependable.',
        ],
        bullets: [
          'Scheduling that considers skills, service area, duration, parts, and customer commitments.',
          'Mobile job context with history, access notes, checklists, photos, and structured closeout.',
          'Estimates and approvals that move clearly from customer decision to scheduled work.',
          'Role-specific access for owners, dispatchers, technicians, apprentices, and customers.',
          'A searchable record of what was done, what changed, and what should happen next.',
        ],
      },
      {
        heading: 'Build for the language your crew uses',
        paragraphs: [
          'Atlas AG\'s production work repeatedly starts with vocabulary. MaintainTime uses equipment records, service intervals, QR access, hours, mileage, and operator roles because those are the concepts that make maintenance usable in the field. A trade contractor needs the same discipline around its own terms: call types, zones, systems, job stages, approvals, and definitions of complete.',
          'This is why a short, focused custom system can outperform a large platform. The advantage is not more screens. It is that the important screen says the right thing to the right person at the right time.',
        ],
      },
      buildGuidance,
      {
        heading: 'Scope the handoff that is costing you',
        paragraphs: [
          'Bring a sample of the last ten jobs, including the ones that went well and the ones that required extra calls. Map what the customer said, what the office needed, what the technician saw, and what the business needed to know afterward. That is enough to identify a first release without pretending every process must change at once.',
          'Paid discovery is $3,500 for two weeks, credited against the build. Atlas AG produces the spec, wireframes, and fixed quote, then works toward a production release in six to ten weeks. Ownership stays with the client, and managed support is optional.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'Compare build versus buy for field operations', href: '/build-vs-buy-field-operations' },
      { label: 'Read questions to ask before hiring a developer', href: '/questions-before-hiring-a-developer' },
      { label: 'Describe your trade contractor workflow', href: '/#contact' },
    ],
  },
  {
    path: '/what-custom-software-costs',
    kind: 'guide',
    title: 'What Custom Software Costs | Atlas AG',
    description:
      'A direct guide to custom software pricing: paid discovery, single-workflow tools, operational platforms, SaaS products, marketing sites, ownership, and hosting.',
    eyebrow: 'BUYING GUIDE / COST',
    h1: 'What custom software actually costs.',
    intro:
      'Custom software is not one price because a focused internal workflow and a multi-tenant product are not the same build. The useful answer is a range tied to the first release, the roles involved, the integrations required, and whether the software is for one business or for sale to many customers.',
    directAnswer:
      'Custom builds start at $15,000. A focused single-workflow tool typically runs $15,000 to $25,000; an operational platform with roles, notifications, payments, and mobile typically runs $35,000 to $60,000; and a multi-tenant SaaS product intended for sale starts at $60,000 and up.',
    sections: [
      {
        heading: 'The ranges',
        paragraphs: [
          'A single-workflow tool is the smallest useful custom build. It might replace a fragile spreadsheet, coordinate one operational handoff, or give a team a focused internal interface. That range is typically $15,000 to $25,000 when the workflow, users, and first release are clear.',
          'An operational platform becomes more involved when it has multiple roles, notifications, payments, mobile use, customer access, or integrations. Those builds typically run $35,000 to $60,000. A product intended to serve multiple companies as a multi-tenant SaaS product starts at $60,000 and up because it needs product-level account boundaries, onboarding, billing, and a more durable support model.',
        ],
      },
      {
        heading: 'Paid discovery comes first',
        paragraphs: [
          'Atlas AG offers a two-week paid discovery for $3,500. It produces a written specification, wireframes, and a fixed quote for the build. The discovery fee is credited against the build, so it is not a second fee for the same work when a project moves forward.',
          'Discovery is where the first release becomes specific. It separates a must-have workflow from a future wish list, identifies the people who need access, maps the important data, and makes a date possible. A fixed price is only useful when the boundaries behind it are visible.',
        ],
      },
      {
        heading: 'What moves a project between ranges',
        paragraphs: [
          'The biggest cost drivers are usually not the number of pages. They are the number of workflows, roles, integrations, and decisions the product must support. A system that connects a field crew, manager, customer, payment provider, and external data source has more coordination to get right than a system used by one person.',
        ],
        bullets: [
          'Number of distinct workflows in the first release.',
          'Roles, permissions, approvals, and customer-facing access.',
          'Payments, email, SMS, mapping, AI, or other third-party services.',
          'Data migration and the quality of the records being brought over.',
          'Mobile field use, offline constraints, reporting, and audit requirements.',
          'Whether the system is owned by one business or must support many tenants.',
        ],
      },
      {
        heading: 'Marketing sites are a different engagement',
        paragraphs: [
          'Atlas AG also takes on marketing and brochure sites when capacity allows. Those projects are billed at $225 per hour with a not-to-exceed cap quoted up front, plus $50 per month for hosting. Once a site needs user accounts, a customer portal, or an admin dashboard, it becomes a platform build starting at $15,000.',
          'That distinction protects both sides. A marketing site can be scoped as a bounded communication project. An operational product needs discovery, behavior, roles, data, and a release plan.',
        ],
      },
      buildGuidance,
      {
        heading: 'Ownership is part of the price',
        paragraphs: [
          'Clients own their code, database, and customer data outright. By default, finished software is deployed to infrastructure the client owns. Atlas AG Managed is available as a separate monthly service for hosting, monitoring, updates, and support when a client does not want to run those responsibilities internally.',
          'Metered services such as AI, SMS, email, mapping, and payments are billed directly by the vendor to the client. That keeps usage visible and keeps third-party accounts under the client\'s control instead of hiding them inside an opaque software fee.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'See how long a custom build takes', href: '/how-long-custom-software-takes' },
      { label: 'Compare building with buying for field operations', href: '/build-vs-buy-field-operations' },
      { label: 'Ask for a fixed quote', href: '/#contact' },
    ],
  },
  {
    path: '/build-vs-buy-field-operations',
    kind: 'guide',
    title: 'Build vs Buy for Field Operations | Atlas AG',
    description:
      'A practical build-versus-buy guide for field operations: when configurable software is enough, when custom software fits better, and how to decide.',
    eyebrow: 'BUYING GUIDE / DECISION',
    h1: 'Build vs. buy for field operations.',
    intro:
      'Buy software when a product already matches the workflow closely enough that the business can adopt it without losing important decisions. Build software when the workflow is the advantage, the available products force costly workarounds, and ownership of the process matters more than a fast generic setup.',
    directAnswer:
      'Buy when the operation can change its workflow to fit a proven product. Build when the software must fit a workflow that is specific, operationally important, and poorly served by off-the-shelf tools. The decision should be made around the cost of workarounds, not the sticker price alone.',
    sections: [
      {
        heading: 'When buying is the right answer',
        paragraphs: [
          'A good off-the-shelf product is a strong choice when the business has a common workflow, needs to start quickly, and can use the product\'s terms and rules without creating a parallel process. Buying also makes sense when the workflow is not a competitive differentiator and a mature vendor handles the boring parts well.',
          'The test is practical: can the team schedule, record, approve, communicate, and report without keeping a shadow spreadsheet? If yes, the software may be doing enough. The business should understand its per-seat cost, data export story, integrations, and what happens when the vendor changes a feature or price.',
        ],
      },
      {
        heading: 'When building is the right answer',
        paragraphs: [
          'Custom software becomes worth considering when the workflow is the business. That is common in field operations where scheduling, equipment, crews, customers, compliance, and service conditions interact in a way that a generic product cannot represent cleanly.',
          'A build is also a better fit when a team spends meaningful time translating between tools, when the people doing the work avoid the official system, or when a missed handoff creates a disproportionate cost. Those are signals that the business is already paying for custom behavior, just inefficiently and without owning the result.',
        ],
      },
      {
        heading: 'The field-work test',
        paragraphs: [
          'Walk one complete job or service cycle. Start with the request, follow it through planning and field execution, then follow the record into billing, maintenance, reporting, or the next decision. Mark every place where a person copies information, asks for status, changes vocabulary, or keeps a note outside the official system.',
        ],
        bullets: [
          'Does the person closest to the work have a fast way to record the important fact?',
          'Can the office see exceptions without calling for every update?',
          'Do roles need different views, permissions, or approval steps?',
          'Would changing the workflow be more expensive than changing the software?',
          'Does the business need to own the data and behavior for the long term?',
        ],
      },
      {
        heading: 'Use a small first release',
        paragraphs: [
          'Build-versus-buy is not a choice between adopting nothing and building an entire enterprise suite. A focused custom release can replace the handoff that costs the most, while the business continues using existing tools around it. That creates a useful test without committing to a five-year transformation.',
          'MaintainTime illustrates the principle. Equipment operations do not need a generic inventory screen; they need service intervals by hours, mileage, or calendar, QR access for field crews, service history, alerts, and roles that match who touches the machine. The value comes from the fit between the workflow and the interface.',
        ],
      },
      buildGuidance,
      {
        heading: 'Compare total cost, not license cost',
        paragraphs: [
          'Buying has a visible subscription and an invisible adoption cost. Building has a visible project price and a different responsibility for support. Compare the time spent on workarounds, the cost of errors and delays, per-seat growth, integration limits, data ownership, and the value of making the operation easier to run.',
          'Atlas AG starts with a $3,500 paid discovery that produces wireframes, a specification, and a fixed quote credited against the build. The result should make the decision clearer even when the answer is to buy: you will understand the workflow, the boundaries, and what the software truly needs to do.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'Read why off-the-shelf software fails field work', href: '/why-off-the-shelf-fails-field-operations' },
      { label: 'See the custom software cost ranges', href: '/what-custom-software-costs' },
      { label: 'Talk through a field operation', href: '/#contact' },
    ],
  },
  {
    path: '/why-off-the-shelf-fails-field-operations',
    kind: 'guide',
    title: 'Why Off-the-Shelf Software Fails Field Work | Atlas AG',
    description:
      'Why generic software breaks down in field operations, from mobile conditions and role mismatch to per-seat pricing, vocabulary, and lost handoffs.',
    eyebrow: 'BUYING GUIDE / FIELD OPERATIONS',
    h1: 'Why off-the-shelf software fails field work.',
    intro:
      'Off-the-shelf software fails field work when it models the office version of the operation instead of the physical one. A product can have every expected feature and still lose because a crew cannot use it quickly, a manager cannot see the exception, or the workflow requires a translation nobody has time to perform.',
    directAnswer:
      'Generic software fails field work when its assumptions about connectivity, roles, vocabulary, data entry, and pricing do not match the operation. The fix is not always custom software, but the failure should be measured by the workarounds the business is already paying for.',
    sections: [
      {
        heading: 'The mobile screen is part of the workflow',
        paragraphs: [
          'A field user may be wearing gloves, moving between machines, standing in weather, sharing a device, or working with limited attention. A desktop workflow compressed onto a phone is still a desktop workflow. If the user has to search, remember an ID, or fill out a long form to record one important fact, the official system will lose to a text message or paper.',
          'Good field software treats context as a design constraint. The asset can be reached by a QR code. The common action is obvious. The record saves partial progress when appropriate. The interface does not ask a crew to become an administrator before it lets them do the job.',
        ],
      },
      {
        heading: 'Roles are not just permissions',
        paragraphs: [
          'A permission matrix determines what a user may access. A useful field product also determines what the user needs to see and what decision they are responsible for. An operator, mechanic, supervisor, dispatcher, customer, and owner may all touch the same record, but they should not receive the same screen.',
          'When a generic product exposes too much, field adoption drops. When it exposes too little, staff create a parallel record. Custom work can make roles part of the workflow so the product reflects how responsibility actually moves through the operation.',
        ],
      },
      {
        heading: 'Vocabulary is operational data',
        paragraphs: [
          'Every business has words that carry more meaning than their generic equivalent. A service call, load, treatment, inspection, return, crew, unit, or closeout may have a specific definition and next step. If the software calls it something else, people translate in their heads and reports become less trustworthy.',
          'The product should use the company\'s language where it affects decisions. That does not mean every internal phrase needs a custom label. It means the important concepts should map to the real operation rather than a vendor\'s marketing taxonomy.',
        ],
      },
      {
        heading: 'Per-seat pricing amplifies a mismatch',
        paragraphs: [
          'Field operations often have seasonal staff, subcontractors, shared devices, occasional users, and customers who need access to one part of the workflow. A broad per-seat plan can make the right behavior financially awkward, so companies leave people out or share accounts. Both choices damage accountability and data quality.',
          'A fit-built system can still have operating costs, but the commercial model can reflect the actual use. More importantly, the business owns the code, database, and customer data rather than paying a permanent tax for access to its own process.',
        ],
      },
      {
        heading: 'A better response',
        paragraphs: [
          'Start by documenting the failure, not by listing every feature missing from the current product. Find the handoff where a delay, wrong status, or missing record becomes expensive. Build that path with the field user, then connect the office view and the next decision.',
          'MaintainTime follows this pattern with equipment records, service intervals, QR access, fuel tracking, history, alerts, and owner, manager, and operator roles. The system is useful because each feature answers a real question in the work, not because it recreates a generic fleet dashboard.',
        ],
      },
      buildGuidance,
      {
        heading: 'Do not customize around a broken assumption',
        paragraphs: [
          'Before buying add-ons or commissioning a build, test the workflow in the conditions where it has to succeed. Put the phone in the field, ask the actual crew to use it, and trace the resulting record back to the office. The gaps that remain are more valuable than a vendor demo.',
          'If the operation needs a custom system, Atlas AG uses a $3,500 two-week discovery to produce wireframes, a specification, and a fixed quote credited against the build. Most first releases go live in six to ten weeks, with the client owning the finished infrastructure by default.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'Compare build and buy for field operations', href: '/build-vs-buy-field-operations' },
      { label: 'See custom field software in production', href: 'https://maintaintime.com' },
      { label: 'Describe the field workflow that is failing', href: '/#contact' },
    ],
  },
  {
    path: '/questions-before-hiring-a-developer',
    kind: 'guide',
    title: 'Questions Before Hiring a Software Developer | Atlas AG',
    description:
      'The practical questions to ask before hiring a software developer: scope, ownership, pricing, timeline, hosting, support, third parties, and delivery.',
    eyebrow: 'BUYING GUIDE / DUE DILIGENCE',
    h1: 'Questions to ask before hiring a software developer.',
    intro:
      'A good software engagement should make the business clearer, not more dependent on a person who speaks a different technical language. Before hiring a developer or studio, ask how the first release will be defined, what you will own, how price and date are controlled, where the software will run, and what happens after launch.',
    directAnswer:
      'Ask for a specific first release, a written scope, ownership of code and data, a fixed quote where possible, a realistic timeline, a clear hosting model, and an honest explanation of third-party costs. If those answers stay vague, the project is not ready to start.',
    sections: [
      {
        heading: 'What exactly will be built first?',
        paragraphs: [
          'The first release should be described in terms of users, workflows, decisions, and a usable outcome. “Build a platform” is not scope. “A dispatcher can schedule crews, a crew can see the day and submit a completion record, and a manager can review exceptions” is closer to a release boundary.',
          'Ask what is intentionally not included. A capable developer can always suggest more features. The quality of the engagement shows up in the choices that keep the first release small enough to ship and useful enough to matter.',
        ],
      },
      {
        heading: 'Who owns the result?',
        paragraphs: [
          'Ask directly whether the client owns the source code, database, customer data, deployment configuration, and accounts for third-party services. Clarify what happens if the relationship ends. Ownership should not depend on continuing to pay a vendor for access to the product the business funded.',
          'Atlas AG treats ownership as a default: clients own their code, database, and customer data outright. Finished software is deployed to infrastructure the client owns, while Atlas AG Managed is available separately for clients who want hosting, monitoring, updates, and support handled for them.',
        ],
      },
      {
        heading: 'How are price and changes handled?',
        paragraphs: [
          'Ask whether the project is fixed scope, fixed price, and fixed date, or whether the developer expects open-ended hourly billing. Hourly work can be appropriate for small marketing changes, but an operational platform needs a shared boundary before the build begins.',
          'Atlas AG uses paid discovery for $3,500 over two weeks. The deliverable is a specification, wireframes, and a fixed quote, and the discovery fee is credited against the build. Marketing sites are a separate engagement billed at $225 per hour with a not-to-exceed cap quoted up front, plus $50 per month for hosting.',
        ],
      },
      {
        heading: 'How will we use it while it is being built?',
        paragraphs: [
          'A project should not disappear into a black box for six months. Ask how often working software will be available, who will review it, how feedback becomes a decision, and what the client must provide. Atlas AG puts working software in front of the client weekly so the product is tested against the real workflow before launch.',
          'That cadence is not a substitute for scope. It is how a clear scope stays honest. The client can catch a vocabulary problem, a missing role, or an awkward field interaction while changing it is still reasonable.',
        ],
      },
      {
        heading: 'What happens after launch?',
        paragraphs: [
          'Ask who owns deployment, monitoring, updates, backups, support, and vendor accounts. Ask what is included in the handoff and whether the client can run the software without the original developer. Third-party services such as AI, SMS, email, mapping, and payments should be visible and billed directly by their vendors to the client.',
          'Most custom builds go live in six to ten weeks once the first release is defined. Ongoing support can continue after launch, but it should be an option the client chooses rather than a condition of ownership.',
        ],
      },
      {
        heading: 'A short checklist',
        paragraphs: [
          'The right developer will answer these questions without turning them into a sales performance. They will explain tradeoffs in plain language and tell you when a build is not the right answer.',
        ],
        bullets: [
          'What operational problem will the first release solve?',
          'What is explicitly out of scope?',
          'What do we own at handoff?',
          'What are the fixed price and target date?',
          'Who pays for infrastructure and metered services?',
          'How will we review working software each week?',
          'What support is optional after the release?',
        ],
      },
    ],
    relatedLinks: [
      { label: 'See the custom software cost guide', href: '/what-custom-software-costs' },
      { label: 'Read how long a custom build takes', href: '/how-long-custom-software-takes' },
      { label: 'Ask Atlas AG about your project', href: '/#contact' },
    ],
  },
  {
    path: '/how-long-custom-software-takes',
    kind: 'guide',
    title: 'How Long Custom Software Takes | Atlas AG',
    description:
      'How long a custom software build takes, from two-week paid discovery through a six-to-ten-week production release and handoff.',
    eyebrow: 'BUYING GUIDE / TIMELINE',
    h1: 'How long a custom software build takes.',
    intro:
      'Most Atlas AG custom software builds go live in six to ten weeks after the first release is defined. The calendar depends less on the number of screens than on the number of workflows, roles, integrations, data decisions, and people who need to review the result.',
    directAnswer:
      'Plan for two weeks of paid discovery, then typically six to ten weeks for a focused production build. A small single-workflow tool can move faster; a multi-role platform, migration, or SaaS product can take longer when its first release has more behavior to prove.',
    sections: [
      {
        heading: 'The two weeks before the build',
        paragraphs: [
          'Atlas AG starts with paid discovery at $3,500. Over two weeks, the team maps the operational problem, identifies the first release, produces wireframes, and writes a specification that can support a fixed quote. The fee is credited against the build.',
          'This stage is not an open-ended strategy exercise. It is a decision-making period. The output should tell the client what will be built, what will wait, what information is needed, and what the team can reasonably put into production.',
        ],
      },
      {
        heading: 'Weeks one and two: establish the working path',
        paragraphs: [
          'The build starts with the foundation that makes the workflow testable: the main records, roles, first screens, and the path from input to useful result. The client should see the product early, even if the edges are unfinished.',
          'Early review is where the team checks vocabulary, permissions, and the shape of the workflow. It is much cheaper to learn that a crew needs a QR entry point or a manager needs an exception list before every secondary feature is built.',
        ],
      },
      {
        heading: 'Weeks three through six: connect the operation',
        paragraphs: [
          'The middle of a build connects the happy path to reality. Notifications, approvals, customer access, payments, mobile behavior, imports, and the integrations that make the system useful get tested against actual scenarios. The product should be usable enough for the client to react to the work, not just to screenshots.',
          'Atlas AG builds in front of the client weekly. That creates a steady feedback loop without making every preference a scope change. The first release remains the boundary, while real use exposes the decisions that need to be clarified.',
        ],
      },
      {
        heading: 'Weeks seven through ten: harden and ship',
        paragraphs: [
          'The final part of a focused build is about reliability, permissions, edge cases, documentation, and the handoff. The client uses the real flow, checks the important records, and prepares the people who will operate it. A release is not finished because the interface looks complete; it is finished when the business can use it without the developer narrating every step.',
          'A larger platform, significant data migration, complex third-party integration, or product intended for sale as SaaS can require a longer schedule. The right response is to split the work into releases rather than hide the uncertainty inside an unbounded timeline.',
        ],
      },
      {
        heading: 'What makes a project take longer',
        paragraphs: [
          'The schedule usually changes when decisions stay unresolved or when the first release keeps absorbing future ideas. External approvals, missing source data, vendor account setup, and a large number of roles can also affect the calendar.',
        ],
        bullets: [
          'Unclear ownership of the workflow or product decisions.',
          'A first release that includes several independent operations.',
          'Data that needs cleaning or migration before it can be trusted.',
          'Integrations with vendors whose access or behavior is not yet known.',
          'Late changes to roles, permissions, pricing, or customer-facing behavior.',
        ],
      },
      {
        heading: 'A fast build is not a rushed build',
        paragraphs: [
          'Speed comes from choosing a useful first release, keeping feedback close to working software, and making decisions before they become dependencies. It does not come from skipping ownership, documentation, security, or the field conditions that make the product usable.',
          'Atlas AG uses fixed scope, fixed price, and fixed date for custom platform builds. Clients own the code, database, and customer data. Finished software is deployed to client-owned infrastructure by default, with Atlas AG Managed available as a separate monthly service.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'See what custom software costs', href: '/what-custom-software-costs' },
      { label: 'Read questions before hiring a developer', href: '/questions-before-hiring-a-developer' },
      { label: 'Plan a first release with Atlas AG', href: '/#contact' },
    ],
  },
  {
    path: '/work/maintaintime',
    kind: 'case-study',
    title: 'MaintainTime Equipment Maintenance Software | Atlas AG',
    description:
      'How Atlas AG built MaintainTime, an equipment maintenance platform for operations that run on machines, with field access, service history, alerts, and roles.',
    eyebrow: 'CASE STUDY / MAINTAINTIME',
    h1: 'MaintainTime: maintenance records that reach the machine.',
    intro:
      'MaintainTime is equipment maintenance tracking for operations that run on machines. The product is built around a practical question: can the right person record and find the right maintenance information at the moment the equipment is being used?',
    sections: [
      {
        heading: 'The operational problem',
        paragraphs: [
          'Equipment maintenance is easy to postpone when the record is somewhere else. An owner wants a complete service history. A manager needs to know what is due. An operator needs a fast way to report a fact from the field. A mechanic needs the context behind the next service. If those people are working from separate notes, the operation finds out about a missed interval when a machine is already unavailable.',
          'The product needed to be useful across that chain without becoming a generic enterprise fleet system. The record had to stay attached to the equipment, the workflow had to work on a phone, and the system had to make a preventive action visible before a failure.',
        ],
      },
      {
        heading: 'The decisions that shaped the product',
        paragraphs: [
          'Service intervals are not all calendar reminders. MaintainTime supports intervals by hours, mileage, or calendar so the rule can follow the machine and the operation. Fuel tracking and full service history stay with the same asset record, creating context instead of a series of disconnected events.',
          'QR codes make the field entry point physical. An operator can scan the machine rather than search a list or remember an internal identifier. The product runs as a web app on a phone, which means a twenty-person crew does not need an app-store installation. English and Spanish throughout the product make the workflow more usable across the people doing the work.',
        ],
      },
      {
        heading: 'What shipped',
        paragraphs: [
          'MaintainTime gives operations a shared place for equipment records, service intervals, fuel, history, and alerts before something breaks. Owner, manager, and operator roles reflect who actually touches what, while the field entry point stays short enough to use in context.',
          'The result is not just a maintenance calendar. It is an operational record that can answer which machine needs attention, what happened previously, and who is responsible for the next action.',
        ],
        bullets: [
          'Service intervals by hours, mileage, or calendar.',
          'Fuel tracking and full service history.',
          'Automated alerts before a service issue becomes a breakdown.',
          'QR code access from the equipment in the field.',
          'English and Spanish support throughout the product.',
          'Owner, manager, and operator roles.',
        ],
      },
      {
        heading: 'What it runs on',
        paragraphs: [
          'MaintainTime is a web product built with React, Express, PostgreSQL, and Stripe. The web approach keeps the product available on a phone without requiring an app-store install, while the database keeps the equipment record available to the roles that need it.',
          'The technical choices serve the operating model. The important part is not the stack as a badge; it is that the product can support an asset record, role-aware workflows, notifications, and client-controlled data without asking the field crew to work like office staff.',
        ],
      },
      {
        heading: 'Timeline and the lesson',
        paragraphs: [
          'Atlas AG plans focused production releases in a six-to-ten-week window after discovery defines the first release. The lesson from MaintainTime is that operational specificity compounds: a QR code, the right interval types, a useful alert, and a role model can make a small product feel like it belongs to the work.',
          'That is the standard for a custom build. Start with the moment where a missed record costs the most, then design the system around the person who has to act.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'Visit MaintainTime', href: 'https://maintaintime.com' },
      { label: 'Read the logging operations software guide', href: '/software-for-logging-operations' },
      { label: 'Build a workflow around your equipment', href: '/#contact' },
    ],
  },
  {
    path: '/work/withease',
    kind: 'case-study',
    title: 'WithEase Regulated Operations Platform | Atlas AG',
    description:
      'How Atlas AG built the shared WithEase platform for financial advisors, mortgage brokers, and insurance agents, then specialized it for each workflow.',
    eyebrow: 'CASE STUDY / WITHEASE',
    h1: 'WithEase: one platform, three regulated industries.',
    intro:
      'WithEase is a shared operational platform specialized for financial advisors, mortgage brokers, and insurance agents. It demonstrates a product decision Atlas AG brings to client work: build a durable core once, then respect the real differences between regulated professions instead of flattening them into one generic workflow.',
    sections: [
      {
        heading: 'The operational problem',
        paragraphs: [
          'Client-facing professional services share a broad shape: a relationship starts, information is collected, work moves through stages, people need a current view, and compliance matters. The details are different enough that a financial advisor, mortgage broker, and insurance agent cannot simply use the same labels and approval path.',
          'A platform built separately for each profession can duplicate the same foundations. A single generic product can erase the distinctions that make the work safe and usable. WithEase sits between those extremes: a shared core engine with specializations for each vertical\'s workflow and compliance requirements.',
        ],
      },
      {
        heading: 'The decisions that shaped the product',
        paragraphs: [
          'The key decision was architectural and operational at the same time. Common capabilities should be built once so they remain consistent and maintainable. The workflows on top of those capabilities should be allowed to speak the language of the profession and enforce the steps that profession requires.',
          'That separation is what makes one platform serve three regulated professions without pretending they are interchangeable. It also creates a useful pattern for a client who has several teams, business lines, or markets: share what should be shared, and keep the domain-specific decisions explicit.',
        ],
      },
      {
        heading: 'What shipped',
        paragraphs: [
          'WithEase shipped as client-facing operations for financial advisors, mortgage brokers, and insurance agents. AdviseWithEase, BrokerWithEase, and InsureWithEase expose the specialization while the underlying approach remains shared.',
          'The result is a platform model rather than three unrelated builds. Each vertical can have the workflow and compliance behavior it needs, while the shared foundation keeps the product family coherent and gives future changes a place to land.',
        ],
        bullets: [
          'One core engine serving three regulated professions.',
          'Financial advisor operations through AdviseWithEase.',
          'Mortgage broker operations through BrokerWithEase.',
          'Insurance agent operations through InsureWithEase.',
          'Vertical-specific workflow and compliance requirements.',
        ],
      },
      {
        heading: 'What it runs on',
        paragraphs: [
          'The important technical approach is the boundary between shared platform behavior and vertical specialization. A product like WithEase needs clear data and permission boundaries, reusable foundations, and enough flexibility that a change for one profession does not silently alter another.',
          'Atlas AG applies the same reasoning to custom client work. A first release should not over-generalize too early, but it should also avoid baking every shared capability into one vertical-specific corner. Good product architecture makes the next specialization cheaper without making the current one vague.',
        ],
      },
      {
        heading: 'Timeline and the lesson',
        paragraphs: [
          'Atlas AG plans focused production releases in six to ten weeks once discovery defines the first workflow. The WithEase lesson is that platform thinking is not the same as building a giant system up front. It is identifying which parts deserve a shared core and which parts must remain close to the work.',
          'For a business with multiple roles or related operating units, that distinction can be the difference between a custom product that compounds in value and a set of disconnected tools that all need the same change made twice.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'Visit AdviseWithEase', href: 'https://advisewithease.com' },
      { label: 'Visit BrokerWithEase', href: 'https://brokerwithease.com' },
      { label: 'Visit InsureWithEase', href: 'https://inswithease.com' },
      { label: 'Build a platform around your real roles', href: '/#contact' },
    ],
  },
  {
    path: '/work/insight-advantage',
    kind: 'case-study',
    title: 'Insight Advantage Competitive Intelligence | Atlas AG',
    description:
      'How Atlas AG built Insight Advantage to monitor ten data sources, produce a weekly AI-written digest, and support 50 industry-specific landing pages.',
    eyebrow: 'CASE STUDY / INSIGHT ADVANTAGE',
    h1: 'Insight Advantage: useful intelligence without another dashboard to babysit.',
    intro:
      'Insight Advantage is competitive intelligence for local businesses. It monitors ten data sources and delivers a weekly AI-written digest, turning a scattered stream of changes into something an owner can review and act on.',
    sections: [
      {
        heading: 'The operational problem',
        paragraphs: [
          'Local businesses need to know what is changing around them, but the information is distributed across sources that were not designed to be read together. Competitor activity, listings, content, and other signals arrive at different rhythms. Watching them manually takes time away from running the business, while a raw feed creates more noise than insight.',
          'The product opportunity was to make monitoring persistent and interpretation useful. An owner should not have to hire someone to watch ten sources or learn a new analytics dashboard every morning. They need a concise view of what changed and why it may matter.',
        ],
      },
      {
        heading: 'The decisions that shaped the product',
        paragraphs: [
          'The system monitors ten data sources, then uses AI to write a weekly digest. The AI has a defined job: summarize the monitored change into a readable update. It is not a chat box bolted onto the side of the product and it is not asked to replace the owner\'s judgment.',
          'That distinction matters in production AI. A model is most useful when it sits inside a clear workflow with a known input, a useful transformation, and a human decision at the end. The product makes the monitoring reliable first, then uses AI where interpretation earns its place.',
        ],
      },
      {
        heading: 'What shipped',
        paragraphs: [
          'Insight Advantage monitors ten data sources and delivers a weekly AI-written digest for local businesses. It also supports 50 industry-specific landing pages, giving the product a content layer that can speak directly to distinct markets rather than one generic homepage.',
          'The result connects data collection, interpretation, and distribution. A business gets a regular answer to “what changed around me?” without having to maintain a research process of its own.',
        ],
        bullets: [
          'Monitoring across ten data sources.',
          'A weekly AI-written digest for owners.',
          'A production workflow that uses AI for summarization.',
          'Fifty industry-specific landing pages.',
          'A focus on local business competitive intelligence.',
        ],
      },
      {
        heading: 'What it runs on',
        paragraphs: [
          'The core technical approach is a pipeline: gather source data, identify meaningful changes, produce a digest, and deliver it on a dependable cadence. The AI step is one part of that pipeline, which keeps it testable and makes it possible to improve the surrounding product without pretending the model is the whole system.',
          'Atlas AG uses the same approach when adding AI to client software. Start with the operational job, define the source and the useful output, keep accounts and usage visible, and make sure a person can understand the result.',
        ],
      },
      {
        heading: 'Timeline and the lesson',
        paragraphs: [
          'Atlas AG plans focused production releases in six to ten weeks once the first release is scoped. Insight Advantage shows how a product can turn a repeated attention problem into a service: monitor continuously, summarize deliberately, and deliver at a rhythm the customer can use.',
          'It also shows why content and product should not be separated. The 50 industry-specific landing pages are not filler; they give a focused product a way to answer the language of distinct markets.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'Visit Insight Advantage', href: 'https://insightadvantage.io' },
      { label: 'Read how Atlas AG uses AI in production', href: '/#capability' },
      { label: 'Build a product with AI doing a real job', href: '/#contact' },
    ],
  },
  {
    path: '/work/emailbridge',
    kind: 'case-study',
    title: 'EmailBridge Custom Domain Email | Atlas AG',
    description:
      'How Atlas AG built EmailBridge on Cloudflare Email Routing to provide custom domain email for two dollars a month, with an MCP server.',
    eyebrow: 'CASE STUDY / EMAILBRIDGE',
    h1: 'EmailBridge: custom domain email without a mailbox-provider bill.',
    intro:
      'EmailBridge provides custom domain email for two dollars a month. It is built on Cloudflare Email Routing rather than a traditional mailbox provider, sends and receives through an existing inbox, and includes an MCP server so AI assistants can work with the inbox directly.',
    sections: [
      {
        heading: 'The operational problem',
        paragraphs: [
          'Many small businesses want email at their own domain but do not need another full mailbox platform for every address. They need custom-domain identity, reliable routing, and a simple setup that works with the inbox they already use. Traditional mailbox pricing can make that small requirement feel larger than it is.',
          'The other problem is configuration. Domain records, routing rules, filters, and sending behavior can be difficult to make consistent. A low monthly price only helps if the product makes the setup understandable and leaves the business with a system it can operate.',
        ],
      },
      {
        heading: 'The decisions that shaped the product',
        paragraphs: [
          'EmailBridge uses Cloudflare Email Routing rather than a mailbox provider. That architectural decision is why the service can cost two dollars a month: it routes custom-domain mail through an existing inbox instead of recreating a hosted mailbox for every user.',
          'The product also automates filter setup so messages sort themselves from day one. That is a small but important example of fit-built software: the value is in the sequence of setup decisions around the underlying service, not in pretending to be a replacement for every email product.',
        ],
      },
      {
        heading: 'What shipped',
        paragraphs: [
          'EmailBridge sends and receives from a custom domain through an existing inbox, with automated filter setup and a simple operating model. It includes an MCP server, giving AI assistants a direct way to work the inbox instead of requiring a person to click through every task.',
          'The MCP capability extends the product without turning the primary experience into a chat interface. The email still routes through the system the customer understands; an agent can act on it through a defined interface when that is useful.',
        ],
        bullets: [
          'Custom domain email for two dollars a month.',
          'Cloudflare Email Routing as the underlying service.',
          'Sending and receiving through an existing inbox.',
          'Automated filter setup.',
          'An MCP server for direct AI assistant interaction.',
        ],
      },
      {
        heading: 'What it runs on',
        paragraphs: [
          'The product is an integration layer around Cloudflare Email Routing, SMTP relay behavior, and an MCP server. The important engineering decision is to use a focused service boundary rather than build an unnecessary mailbox platform from scratch.',
          'That is a useful pattern for client work too. Custom software does not mean custom-building every primitive. It means choosing the right services, connecting them around the real workflow, and making the result simpler for the people who own it.',
        ],
      },
      {
        heading: 'Timeline and the lesson',
        paragraphs: [
          'Atlas AG plans focused production releases in six to ten weeks once discovery defines the first release. EmailBridge shows how a small product can create a strong value proposition by combining a low-cost underlying service with the setup, defaults, and interface that make it practical.',
          'It also shows where AI integrations are heading. An MCP server is most useful when it gives an agent access to a real operational surface with clear boundaries, not when it adds another generic conversation window.',
        ],
      },
    ],
    relatedLinks: [
      { label: 'Visit EmailBridge', href: 'https://emailbridge.app' },
      { label: 'Read about AI and MCP capability', href: '/#capability' },
      { label: 'Build a focused integration product', href: '/#contact' },
    ],
  },
];

export function getSeoPage(pathname: string) {
  return seoPages.find((page) => page.path === pathname);
}

export function renderPageJsonLd(page: SeoPage) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `https://atlasag.co${page.path}#article`,
        headline: page.h1,
        description: page.description,
        url: `https://atlasag.co${page.path}`,
        author: { '@type': 'Organization', name: 'Atlas AG', url: 'https://atlasag.co/' },
        publisher: { '@type': 'Organization', name: 'Atlas AG', url: 'https://atlasag.co/' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Atlas AG', item: 'https://atlasag.co/' },
          { '@type': 'ListItem', position: 2, name: page.h1, item: `https://atlasag.co${page.path}` },
        ],
      },
    ],
  };
}

export function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderStaticLink(link: SeoPageLink) {
  const externalAttributes = link.href.startsWith('http')
    ? ' target="_blank" rel="noopener noreferrer"'
    : '';
  return `<a href="${escapeHtml(link.href)}"${externalAttributes}>${escapeHtml(link.label)}</a>`;
}

export function renderStaticSeoPage(page: SeoPage) {
  const directAnswer = page.directAnswer
    ? `        <aside class="seo-direct-answer"><strong>Direct answer.</strong> ${escapeHtml(page.directAnswer)}</aside>\n`
    : '';
  const sections = page.sections
    .map(
      (section) => `        <section>
          <h2>${escapeHtml(section.heading)}</h2>
${section.paragraphs.map((paragraph) => `          <p>${escapeHtml(paragraph)}</p>`).join('\n')}
${section.bullets ? `          <ul>\n${section.bullets.map((bullet) => `            <li>${escapeHtml(bullet)}</li>`).join('\n')}\n          </ul>\n` : ''}        </section>`,
    )
    .join('\n');
  const relatedLinks = page.relatedLinks
    .map((link) => `          <li>${renderStaticLink(link)}</li>`)
    .join('\n');

  return `      <main id="crawlable-content">
        <p>${escapeHtml(page.eyebrow)}</p>
        <p><a href="/">ATLAS AG — Software built around your problem</a></p>
        <h1>${escapeHtml(page.h1)}</h1>
        <p>${escapeHtml(page.intro)}</p>
${directAnswer}${sections}
        <section>
          <h2>Continue</h2>
          <ul>
${relatedLinks}
          </ul>
        </section>
        <section>
          <h2>Start a build</h2>
          <p>If you already know what you want built, describe it. If you only know what is broken, that works too.</p>
          <p><a href="/#contact">Tell Atlas AG what needs to exist</a></p>
        </section>
      </main>`;
}